// src/lib/suggestions.ts

type SuggestionEvent = { name: string; ts: number; kind: 'search' | 'add' | 'compare' | 'view' | 'price' };

const RECENT_SEARCH_KEY = 'bw.suggest.recentSearches';
const RECENT_ACTION_KEY = 'bw.suggest.recentActions';
const PREF_KEY = 'bw.suggest.pref.v1';
const PRICE_NOTIFY_KEY = 'bw.suggest.priceNotify.v1';

type PrefStore = {
  dish: Record<string, { score: number; last: number }>;
  cuisine: Record<string, number>;
  platform: Record<string, number>;
  hour: Record<string, number>;
  priceBand: Record<string, number>;
};

type PriceNotifyStore = Record<string, number>;

function readList<T>(key: string): T[] {
  try {
    const raw = localStorage.getItem(key);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? (parsed as T[]) : [];
  } catch {
    return [];
  }
}

function writeList<T>(key: string, list: T[]) {
  try {
    localStorage.setItem(key, JSON.stringify(list));
  } catch {}
}

export function recordRecentSearch(query: string) {
  const trimmed = query.trim();
  if (!trimmed) return;
  const list = readList<string>(RECENT_SEARCH_KEY);
  const next = [trimmed, ...list.filter((q) => q.toLowerCase() !== trimmed.toLowerCase())].slice(0, 12);
  writeList(RECENT_SEARCH_KEY, next);
}

export function getRecentSearches(limit = 6): string[] {
  return readList<string>(RECENT_SEARCH_KEY).slice(0, limit);
}

export function recordAction(kind: SuggestionEvent['kind'], name: string) {
  const trimmed = name.trim();
  if (!trimmed) return;
  const list = readList<SuggestionEvent>(RECENT_ACTION_KEY);
  const next = [
    { name: trimmed, kind, ts: Date.now() },
    ...list.filter((e) => e.name.toLowerCase() !== trimmed.toLowerCase()),
  ].slice(0, 20);
  writeList(RECENT_ACTION_KEY, next);
}

export function getActionSeeds(limit = 6): string[] {
  return readList<SuggestionEvent>(RECENT_ACTION_KEY)
    .sort((a, b) => b.ts - a.ts)
    .map((e) => e.name)
    .slice(0, limit);
}

function readPref(): PrefStore {
  try {
    const raw = localStorage.getItem(PREF_KEY);
    if (!raw) return { dish: {}, cuisine: {}, platform: {}, hour: {}, priceBand: {} };
    const parsed = JSON.parse(raw);
    return {
      dish: parsed?.dish || {},
      cuisine: parsed?.cuisine || {},
      platform: parsed?.platform || {},
      hour: parsed?.hour || {},
      priceBand: parsed?.priceBand || {},
    };
  } catch {
    return { dish: {}, cuisine: {}, platform: {}, hour: {}, priceBand: {} };
  }
}

function writePref(next: PrefStore) {
  try {
    localStorage.setItem(PREF_KEY, JSON.stringify(next));
  } catch {}
}

function readPriceNotify(): PriceNotifyStore {
  try {
    return JSON.parse(localStorage.getItem(PRICE_NOTIFY_KEY) || '{}');
  } catch {
    return {};
  }
}

function writePriceNotify(next: PriceNotifyStore) {
  try {
    localStorage.setItem(PRICE_NOTIFY_KEY, JSON.stringify(next));
  } catch {}
}

function priceBucket(price?: number) {
  if (typeof price !== 'number' || !Number.isFinite(price)) return 'unknown';
  if (price <= 150) return 'low';
  if (price <= 300) return 'mid';
  return 'high';
}

function nowHourKey() {
  const h = new Date().getHours();
  if (h < 11) return 'breakfast';
  if (h < 16) return 'lunch';
  if (h < 20) return 'snack';
  return 'dinner';
}

const WEIGHTS: Record<SuggestionEvent['kind'], number> = {
  search: 0.5,
  view: 0.2,
  add: 1,
  compare: 0.6,
  price: 0.7,
};

export function recordPreference(
  kind: SuggestionEvent['kind'],
  name: string,
  meta?: { cuisine?: string; platform?: string; price?: number }
) {
  const trimmed = name.trim();
  if (!trimmed) return;
  const pref = readPref();
  const key = trimmed.toLowerCase();
  const w = WEIGHTS[kind] || 0.2;
  const entry = pref.dish[key] || { score: 0, last: 0 };
  entry.score = Math.min(100, entry.score + w * 10);
  entry.last = Date.now();
  pref.dish[key] = entry;
  if (meta?.cuisine) {
    const c = meta.cuisine.toLowerCase();
    pref.cuisine[c] = (pref.cuisine[c] || 0) + w;
  }
  if (meta?.platform) {
    const p = meta.platform.toLowerCase();
    pref.platform[p] = (pref.platform[p] || 0) + w;
  }
  const hour = nowHourKey();
  pref.hour[hour] = (pref.hour[hour] || 0) + w;
  const band = priceBucket(meta?.price);
  pref.priceBand[band] = (pref.priceBand[band] || 0) + w;
  writePref(pref);
}

export function getPersonalizedSuggestions(limit = 8): string[] {
  const pref = readPref();
  const entries = Object.entries(pref.dish || {});
  const ranked = entries
    .map(([name, val]) => {
      const ageMin = (Date.now() - (val?.last || 0)) / 60000;
      const decay = ageMin > 0 ? Math.max(0.1, 1 - ageMin / (60 * 24 * 7)) : 1;
      return { name, score: (val?.score || 0) * decay };
    })
    .sort((a, b) => b.score - a.score)
    .slice(0, limit * 2)
    .map((r) => r.name);
  return ranked.slice(0, limit).map((n) => n.replace(/\b\w/g, (m) => m.toUpperCase()));
}

export function shouldNotifyPriceDrop(dishName: string, minScore = 6): boolean {
  const key = dishName.toLowerCase();
  const pref = readPref();
  const entry = pref.dish[key];
  const score = entry?.score || 0;
  if (score < minScore) return false;
  const notify = readPriceNotify();
  const last = notify[key] || 0;
  if (Date.now() - last < 6 * 60 * 60 * 1000) return false;
  notify[key] = Date.now();
  writePriceNotify(notify);
  return true;
}
